import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Checkbox, Collapse, Typography } from '@mui/material';
import React, { useState } from 'react';

interface FilterSection {
  id: string;
  label: string;
  options?: string[];
}

const filterSections: FilterSection[] = [
  {
    id: 'teamName',
    label: 'Team Name',
    options: [
      'TCS',
      'Infosys',
      'Wipro',
      'Team Alpha',
      'Stellar',
      'Nova',
      'Orion',
      'Vanguard',
      'Titan',
      'Cosmic',
    ],
  },
  {
    id: 'jobSource',
    label: 'Job Source',
    options: [
      'Tata Consultancy Services',
      'Infosys Limited',
      'Wipro Limited',
      'TechSphere, InnovaTech',
      'Stellar Tech',
      'Nova Systems',
      'Orion Group',
      'Vanguard Industries',
      'Titan Enterprises',
      'Cosmic Innovations',
    ],
  },
  {
    id: 'manager',
    label: 'Manager',
  },
  {
    id: 'businessHead',
    label: 'Business Head',
  },
  {
    id: 'status',
    label: 'Status',
    options: ['Active', 'Inactive'],
  },
];

const TeamFilterSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    teamName: true,
  });

  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string[];
  }>({
    businessHead: [],
    jobSource: [],
    manager: [],
    status: [],
    teamName: [
      'TCS',
      'Infosys',
      'Wipro',
      'Team Alpha',
      'Stellar',
      'Nova',
      'Orion',
      'Vanguard',
      'Titan',
      'Cosmic',
    ],
  });

  const handleToggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleFilterChange = (sectionId: string, option: string) => {
    setSelectedFilters((prev) => {
      const currentSelections = prev[sectionId] || [];
      const isSelected = currentSelections.includes(option);

      return {
        ...prev,
        [sectionId]: isSelected
          ? currentSelections.filter((item) => item !== option)
          : [...currentSelections, option],
      };
    });
  };

  const handleParentCheckboxChange = (
    sectionId: string,
    options: string[] | undefined,
  ) => {
    if (!options) return;

    setSelectedFilters((prev) => {
      const currentSelections = prev[sectionId] || [];
      const allSelected = options.every((opt) =>
        currentSelections.includes(opt),
      );

      return {
        ...prev,
        [sectionId]: allSelected ? [] : options,
      };
    });
  };

  return (
    <Box
      className={`columns-sidebar ${isOpen ? 'expanded' : ''}`}
      onClick={!isOpen ? handleToggleSidebar : undefined}>
      {!isOpen ? (
        <>
          <FilterListIcon className="columns-icon" />
          <Typography className="columns-text">Filters</Typography>
        </>
      ) : (
        <>
          <Box className="sidebar-header" onClick={handleToggleSidebar}>
            <FilterListIcon className="header-icon" />
            <Typography className="header-text">Filters</Typography>
          </Box>

          <Box className="filter-sections">
            {filterSections.map((section) => (
              <Box key={section.id} className="filter-section">
                <Box
                  className="section-header"
                  onClick={() => toggleSection(section.id)}>
                  <ChevronRightIcon
                    className={`expand-icon ${
                      expandedSections[section.id] ? 'expanded' : ''
                    }`}
                  />
                  <Checkbox
                    checked={(() => {
                      if (!section.options) {
                        return (selectedFilters[section.id]?.length || 0) > 0;
                      }
                      const selected = selectedFilters[section.id] || [];
                      return (
                        section.options.length > 0 &&
                        section.options.every((opt) => selected.includes(opt))
                      );
                    })()}
                    indeterminate={(() => {
                      if (!section.options) return false;
                      const selected = selectedFilters[section.id] || [];
                      const selectedCount = section.options.filter((opt) =>
                        selected.includes(opt),
                      ).length;
                      return (
                        selectedCount > 0 &&
                        selectedCount < section.options.length
                      );
                    })()}
                    className="section-checkbox"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleParentCheckboxChange(section.id, section.options);
                    }}
                  />
                  <Typography className="section-label">
                    {section.label}
                  </Typography>
                </Box>

                <Collapse in={expandedSections[section.id]} timeout="auto">
                  {section.options && (
                    <Box className="section-options">
                      {section.options.map((option) => (
                        <Box key={option} className="option-item">
                          <Checkbox
                            checked={selectedFilters[section.id]?.includes(
                              option,
                            )}
                            onChange={() =>
                              handleFilterChange(section.id, option)
                            }
                            className="option-checkbox"
                          />
                          <Typography className="option-label">
                            {option}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Collapse>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TeamFilterSidebar;
